import { __awaiter, __decorate } from "tslib";
import { Auth, Datasource, DatasourceDefaults, FieldTypeEnum, MessageType, ResourceType, dataServerResultFilter, normaliseCriteria, } from '@genesislcap/foundation-comms';
import { LifecycleMixin } from '@genesislcap/foundation-utils';
import { DOM, attr, customElement, observable } from '@microsoft/fast-element';
import { BehaviorSubject } from 'rxjs';
import { debounceTime, skip, tap } from 'rxjs/operators';
import { GridProBaseDatasource } from '../datasource/base.datasource';
import { dateTimeValueFormatter, dateValueFormatter, getFilterParamsByFieldType, } from '../grid-pro.definitions';
import { GridProRendererTypes } from '../grid-pro.types';
import { logger } from '../utils/logger';
const criteriaDelimiter = ';';
const criteriaJoin = ' && ';
const withoutFullInit = null;
const withoutColumnDefs = null;
/**
 * The operation type for the {@link @genesislcap/grid-pro#GridProGenesisDatasource.mapTransaction} method.
 * @internal
 */
export var OperationType;
(function (OperationType) {
    OperationType["Add"] = "add";
    OperationType["Update"] = "update";
    OperationType["Remove"] = "remove";
})(OperationType || (OperationType = {}));
/**
 * The Genesis Datasource element, for CSRM-compatible data fetching and used exclusively by the GridPro element.
 * @remarks Only supports Client-Side Row Model.
 * @public
 */
let GridProGenesisDatasource = class GridProGenesisDatasource extends LifecycleMixin(GridProBaseDatasource) {
    constructor() {
        super(...arguments);
        this.pollingInterval = DatasourceDefaults.REQ_REP_POLLING_INTERVAL_MS;
        this.disablePolling = false;
        this.restartOnReconnection = false;
        this.isRequestServer = false;
        this.requiresFullRowDataAndColDefs = true;
        this.dataSubWasLoggedOff = false;
        this.rows = new Map();
        this.criteriaFromFilters = new Map();
        this.update = new BehaviorSubject(new Map());
    }
    // DatasourceOptions
    criteriaChanged(oldCriteria, newCriteria) {
        const criteriaIsNotDuplicate = oldCriteria !== normaliseCriteria(newCriteria, criteriaDelimiter);
        if (this.datasource.initialized && criteriaIsNotDuplicate) {
            this.reloadResourceData();
        }
    }
    resourceNameChanged(oldValue, newValue) {
        if (!oldValue || oldValue === newValue)
            return;
        this.restart();
    }
    connectedCallback() {
        super.connectedCallback();
        const shouldRunConnect = this.shouldRunConnect;
        DOM.queueUpdate(() => {
            if (!shouldRunConnect)
                return;
            this.init();
        });
        this.connectionSub = this.connect.isConnected$.subscribe((isConnected) => {
            var _a;
            if (isConnected && ((_a = this.agGrid) === null || _a === void 0 ? void 0 : _a.attributes['ds-disconnected'])) {
                this.agGrid.removeAttribute('ds-disconnected');
                if (this.restartOnReconnection) {
                    this.reloadResourceData();
                }
            }
            if (!isConnected) {
                this.agGrid.setAttribute('ds-disconnected', 'ds-disconnected');
            }
        });
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        const shouldRunDisconnect = this.shouldRunDisconnect;
        DOM.queueUpdate(() => {
            if (!shouldRunDisconnect)
                return;
            this.deinit();
        });
    }
    deepClone() {
        const copy = super.deepClone();
        copy.deferredGridOptions = structuredClone(this.deferredGridOptions);
        copy.deferredColumnStates = structuredClone(this.deferredColumnStates);
        copy.request = structuredClone(this.request);
        return copy;
    }
    get agGrid() {
        return this.parentElement;
    }
    // Genesis Specific
    /**
     * Returns the row id attribute, depending on the resource type.
     * @remarks Will favour the `row-id` attribute if set.
     * @internal
     */
    get rowId() {
        if (this.rowIdAttr)
            return this.rowIdAttr;
        if (this.isRequestServer)
            return DatasourceDefaults.REQUEST_SERVER_ROW_ID;
        return DatasourceDefaults.DATASERVER_ROW_ID;
    }
    /**
     * Initializes the datasource.
     * @public
     * @remarks This method is called automatically when the element is connected to the DOM.
     */
    init() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.agGrid &&
                this.datasource.validResourceName(this.resourceName) &&
                this.connect.isConnected) {
                const meta = yield this.connect.getMetadata(this.resourceName);
                if (!meta) {
                    logger.warn(`No metadata found for ${this.resourceName}, datasource will not work. Falling back to local columnDefs/rowData.`);
                    return;
                }
                this.isRequestServer = meta.TYPE === ResourceType.REQUEST_SERVER;
                const gridOptions = Object.assign({ getRowId: (params) => params.data[this.rowId] }, this.deferredGridOptions);
                this.agGrid.gridOptions = Object.assign({}, gridOptions);
                this.agGrid.addEventListener('onGridReady', () => __awaiter(this, void 0, void 0, function* () {
                    yield this.loadResourceData();
                }), { once: true });
                const filterDebounceTime = 600;
                this.updateSub = this.update
                    .pipe(skip(1), debounceTime(filterDebounceTime), tap((f) => logger.debug('filters (debounced): ', f)))
                    .subscribe((_) => {
                    this.reloadResourceData();
                });
                return;
            }
            logger.warn(`Application not connected or invalid resource name, datasource will not work. Falling back to local columnDefs/rowData.`);
        });
    }
    /**
     * Deinitialises the datasource, resetting it to its initial state.
     * @public
     */
    deinit() {
        this.reset();
    }
    /**
     * Resets the datasource to its initial state.
     * @public
     * @deprecated Use `deinit` instead
     */
    reset() {
        if (this.dataSub) {
            this.dataSub.unsubscribe();
            this.dataSub = undefined;
        }
        if (this.updateSub) {
            this.updateSub.unsubscribe();
            this.updateSub = undefined;
        }
        if (this.connectionSub) {
            this.connectionSub.unsubscribe();
            this.connectionSub = undefined;
        }
        this.dataLogoff();
        this.clearRowData();
        this.isRequestServer = false;
        this.requiresFullRowDataAndColDefs = true;
        this.dataSubWasLoggedOff = false;
        this.criteriaFromFilters = new Map();
        this.update = new BehaviorSubject(new Map());
        this.datasource.deinit();
    }
    /**
     * Restarts the datasource, uses `deinit` and `init` in sequence.
     * @public
     */
    restart() {
        this.deinit();
        this.init();
    }
    /**
     * Force the grid to redispatch the current rows
     */
    refreshRows() {
        this.setRowData(Array.from(this.rows.values()));
    }
    /**
     * Sends a DATA_LOGOFF when the resource is an active stream
     * @remarks Will only happen for streaming DATASERER resources.
     * @internal
     */
    dataLogoff() {
        const shouldPerformDataLogoff = this.sourceRef && !this.isRequestServer && !this.isSnapshot;
        if (shouldPerformDataLogoff && this.auth.isLoggedIn) {
            this.connect.dataLogoff(this.sourceRef);
        }
        this.sourceRef = undefined;
    }
    /**
     * Clears the rowData and columnDefs for the grid.
     * @remarks This is called when `reloadResourceData` happens, on criteria and filters change or full `reset`.
     * @param withColumnDefs - Whether to clear the columnDefs as well. Defaults to true.
     * @internal
     */
    clearRowData(withColumnDefs = true) {
        var _a, _b, _c, _d;
        this.rows = new Map();
        this.agTransaction = undefined;
        if (withColumnDefs) {
            (_b = (_a = this.agGrid) === null || _a === void 0 ? void 0 : _a.gridApi) === null || _b === void 0 ? void 0 : _b.setColumnDefs([]);
        }
        (_d = (_c = this.agGrid) === null || _c === void 0 ? void 0 : _c.gridApi) === null || _d === void 0 ? void 0 : _d.setRowData([]);
    }
    /**
     * Sets the columnDefs and rowData for the grid.
     * @remarks This should only happen once, when the grid is first initialized. Follow up updates will use the agTransaction.
     * @internal
     */
    setRowData(rowData) {
        var _a;
        const preSetColumnDefs = (_a = this.agGrid.gridApi) === null || _a === void 0 ? void 0 : _a.getColumnDefs();
        if (!preSetColumnDefs || preSetColumnDefs.length === 0) {
            const agColumnDefs = this.getAgColumnDefs(this.datasource.fieldMetadata);
            if (agColumnDefs) {
                this.agGrid.gridApi.setColumnDefs(agColumnDefs);
            }
        }
        const data = this.rowDataMapper ? this.rowDataMapper(rowData) : rowData;
        this.agGrid.gridApi.setRowData(data);
        this.requiresFullRowDataAndColDefs = false;
    }
    /**
     * Resets the grid data while keeping columnDefs and sends a DATA_LOGOFF message if it's a stream.
     * Proxies to {@link @genesislcap/grid-pro#GridProGenesisDatasource.loadResourceData} but without the full init (no metadata fetch).
     * @remarks This is used when the grid is already initialized and we want to reload the data due to a criteria/filter change.
     * @internal
     */
    reloadResourceData() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            if (this.dataSub) {
                this.dataSub.unsubscribe();
                this.dataSub = undefined;
            }
            this.dataLogoff();
            this.clearRowData(withoutColumnDefs);
            (_b = (_a = this.agGrid) === null || _a === void 0 ? void 0 : _a.gridApi) === null || _b === void 0 ? void 0 : _b.showLoadingOverlay();
            this.loadResourceData(withoutFullInit);
        });
    }
    /**
     * Initializes the datasource and loads the data for the grid.
     * Handles both snapshot and stream data (for both REQUEST_SERVER and DATASERVER resource types).
     *
     * @param withFullInit - if true, will call datasource.init() with requiresMetadataFetch = true, fetching fresh metadata
     * @internal
     */
    loadResourceData(withFullInit = true) {
        return __awaiter(this, void 0, void 0, function* () {
            const requiresMetadataFetch = withFullInit || !this.datasource.initialized;
            const initOK = yield this.datasource.init(this.datasourceOptions(), requiresMetadataFetch);
            if (!initOK) {
                logger.debug(`Genesis Datasource init failed for ${this.resourceName}`);
                this.clearRowData();
                return;
            }
            if (this.isSnapshot) {
                const result = yield this.datasource.snapshot();
                if (result) {
                    const rowData = this.handleSnapshot(result);
                    this.setRowData(rowData);
                }
                return;
            }
            logger.debug(`requesting stream for ${this.resourceName}`);
            this.dataSub = this.datasource.stream.subscribe((result) => {
                this.sourceRef = result.SOURCE_REF;
                const messageType = result.MESSAGE_TYPE;
                if (messageType && messageType === MessageType.LOGOFF_ACK) {
                    this.dataSubWasLoggedOff = true;
                    return;
                }
                this.dataSubWasLoggedOff = false;
                if (result.ROW) {
                    const nextMessage = dataServerResultFilter(result);
                    this.handleStreamResult(nextMessage);
                }
                else {
                    this.handleStreamResult(result);
                }
            });
        });
    }
    handleSnapshot(result) {
        if (Array.isArray(result))
            return result;
        if (result.REPLY) {
            return result.REPLY;
        }
        if (result.ROW) {
            return dataServerResultFilter(result).inserts;
        }
        return result.inserts;
    }
    datasourceOptions() {
        return {
            criteria: this.buildCriteria(),
            fields: this.fields,
            isSnapshot: this.isSnapshot,
            maxRows: this.maxRows,
            maxView: this.maxView,
            movingView: this.movingView,
            orderBy: this.orderBy,
            pollingInterval: this.pollingInterval,
            disablePolling: this.disablePolling,
            request: this.request,
            resourceName: this.resourceName,
            reverse: this.reverse,
        };
    }
    handleStreamResult(result) {
        if (!result)
            return;
        if (this.isRequestServer) {
            this.applyRequestServerData(result);
        }
        else {
            this.applyDataserverData(result);
        }
    }
    applyRequestServerData(requestServerResult) {
        var _a;
        const requestServerData = requestServerResult.REPLY;
        if (!Array.isArray(requestServerData) ||
            !((_a = requestServerResult.MESSAGE_TYPE) === null || _a === void 0 ? void 0 : _a.startsWith('REP_'))) {
            logger.error('received invalid RequestServerResult', requestServerResult);
            return;
        }
        if (this.requiresFullRowDataAndColDefs) {
            requestServerData === null || requestServerData === void 0 ? void 0 : requestServerData.forEach((insertData) => {
                this.rows.set(insertData[this.rowId], insertData);
            });
            const rowData = this.rows.size > 0 ? Array.from(this.rows.values()) : requestServerData;
            this.setRowData(rowData);
            return;
        }
        const rowsToUpdate = [];
        const rowsToDelete = Array.from(this.rows.values()).filter((row) => {
            const match = requestServerData.find((data) => data[this.rowId] === row[this.rowId]);
            if (match) {
                rowsToUpdate.push(match);
                return false; // don't delete rows that need to be updated
            }
            return true; // delete rows that don't exist in requestServerData
        });
        const rowsToInsert = requestServerData.filter((data) => !this.rows.has(data[this.rowId]));
        this.agTransaction = { add: [], remove: [], update: [] };
        this.handleStreamInserts(rowsToInsert);
        this.handleStreamDeletes(rowsToDelete);
        this.handleStreamUpdates(rowsToUpdate);
        this.applyAllAgTransactions();
    }
    applyDataserverData(dataServerResult) {
        var _a;
        if (this.requiresFullRowDataAndColDefs) {
            (_a = dataServerResult.inserts) === null || _a === void 0 ? void 0 : _a.forEach((insertData) => {
                this.rows.set(insertData[this.rowId], insertData);
            });
            const rowData = Array.from(this.rows.values());
            this.setRowData(rowData);
            return;
        }
        this.agTransaction = { add: [], remove: [], update: [] };
        this.handleStreamInserts(dataServerResult.inserts);
        this.handleStreamDeletes(dataServerResult.deletes);
        this.handleStreamUpdates(dataServerResult.updates);
        this.applyAllAgTransactions();
    }
    applyAllAgTransactions() {
        if (!this.agTransaction.add.length &&
            !this.agTransaction.update.length &&
            !this.agTransaction.remove.length) {
            this.agGrid.gridApi.showNoRowsOverlay();
            return;
        }
        if (this.agTransaction.add.length > 0) {
            this.applyMappedAgTransaction({ add: this.agTransaction.add, addIndex: this.agGrid.addIndex }, OperationType.Add, this.agGrid.asyncAdd);
        }
        if (this.agTransaction.remove.length > 0) {
            this.applyMappedAgTransaction({ remove: this.agTransaction.remove }, OperationType.Remove, this.agGrid.asyncRemove);
        }
        if (this.agTransaction.update.length > 0) {
            this.applyMappedAgTransaction({ update: this.agTransaction.update }, OperationType.Update, this.agGrid.asyncUpdate);
        }
    }
    flashAddedCells(rowNodes) {
        if (this.agGrid.enableRowFlashing && rowNodes.length) {
            this.agGrid.gridApi.flashCells({ rowNodes });
        }
    }
    applyMappedAgTransaction(agTransaction, operation, isAsync) {
        if (isAsync) {
            this.agGrid.gridApi.applyTransactionAsync(this.mapTransaction(agTransaction, [operation]), (changedNodes) => {
                this.flashAddedCells(changedNodes.add);
            });
        }
        else {
            const changedNodes = this.agGrid.gridApi.applyTransaction(this.mapTransaction(agTransaction, [operation]));
            this.flashAddedCells(changedNodes.add);
        }
    }
    /**
     * Maps the transaction data to the row data mapper function, if it exists.
     * @param transaction - The transaction data to be mapped.
     * @param operations - The operations to be mapped. Can be 'add', 'update' or 'remove'.
     * @returns The mapped transaction (if the row data mapper function exists), or the original transaction.
     */
    mapTransaction(transaction, operations = Object.values(OperationType)) {
        if (!this.rowDataMapper) {
            return transaction;
        }
        const mappedTransaction = {};
        if (operations.includes(OperationType.Add)) {
            mappedTransaction.add = this.rowDataMapper(transaction.add, this.rows);
        }
        if (operations.includes(OperationType.Remove)) {
            mappedTransaction.remove = this.rowDataMapper(transaction.remove, this.rows);
        }
        if (operations.includes(OperationType.Update)) {
            mappedTransaction.update = this.rowDataMapper(transaction.update, this.rows);
        }
        return mappedTransaction;
    }
    handleStreamInserts(insertedRows) {
        insertedRows === null || insertedRows === void 0 ? void 0 : insertedRows.forEach((insertData) => {
            if (this.rows.has(insertData[this.rowId]) || this.dataSubWasLoggedOff) {
                const rowToBeUpdated = this.rows.get(insertData[this.rowId]);
                const updatedRow = Object.assign(Object.assign({}, rowToBeUpdated), insertData);
                this.agTransaction.update.push(updatedRow);
            }
            else {
                this.agTransaction.add.push(insertData);
                this.agTransaction.addIndex = 0;
            }
            this.rows.set(insertData[this.rowId], insertData);
        });
    }
    handleStreamDeletes(deletedRows) {
        deletedRows === null || deletedRows === void 0 ? void 0 : deletedRows.forEach((deleteData) => {
            this.agTransaction.remove.push({ [this.rowId]: deleteData[this.rowId] });
            this.rows.delete(deleteData[this.rowId]);
        });
    }
    handleStreamUpdates(updatedRows) {
        updatedRows === null || updatedRows === void 0 ? void 0 : updatedRows.forEach((updateData) => {
            const rowToBeUpdated = this.rows.get(updateData[this.rowId]);
            const updatedRow = Object.assign(Object.assign({}, rowToBeUpdated), updateData);
            this.agTransaction.update.push(updatedRow);
            this.rows.set(updateData[this.rowId], updatedRow);
        });
    }
    // FUTURE: Work with the field types vs ag grid column def types!
    getAgColumnDefs(fieldsMetadata) {
        const colDefsFromGenesisData = fieldsMetadata === null || fieldsMetadata === void 0 ? void 0 : fieldsMetadata.flatMap((field) => {
            var _a;
            const colDefBase = {};
            colDefBase.filterParams = getFilterParamsByFieldType(field.type);
            switch (field.type) {
                case FieldTypeEnum.STRING:
                    colDefBase.cellEditor = GridProRendererTypes.stringEditor;
                    break;
                case FieldTypeEnum.NUMBER:
                    colDefBase.cellEditor = GridProRendererTypes.numberEditor;
                    colDefBase.filter = 'agNumberColumnFilter';
                    break;
                case FieldTypeEnum.BOOLEAN:
                    if (this.agGrid.autoCellRendererByType) {
                        colDefBase.cellRenderer = GridProRendererTypes.boolean;
                        colDefBase.cellEditor = GridProRendererTypes.boolean;
                    }
                    break;
                case FieldTypeEnum.DATE:
                    colDefBase.valueFormatter = dateValueFormatter;
                    colDefBase.cellEditor = GridProRendererTypes.dateEditor;
                    colDefBase.filter = 'agDateColumnFilter';
                    break;
                case FieldTypeEnum.DATETIME:
                    colDefBase.valueFormatter = dateTimeValueFormatter;
                    colDefBase.filter = 'agDateColumnFilter';
                    colDefBase.cellEditor = GridProRendererTypes.dateEditor;
                    colDefBase.cellEditorParams = {
                        withTime: true,
                    };
                    break;
                case FieldTypeEnum.ENUM:
                    if (field.enumOptions) {
                        colDefBase.cellEditor = GridProRendererTypes.selectEditor;
                        colDefBase.cellEditorParams = {
                            values: field.enumOptions.map((option) => ({ value: option, label: option })),
                            valueField: 'value',
                            labelField: 'label',
                        };
                    }
                    else {
                        logger.warn(`No enumOptions found for field ${field.name}. Default cell editor applied.`);
                    }
                    break;
                default:
                    break;
            }
            const colDefExtras = (_a = this.agGrid.gridApi) === null || _a === void 0 ? void 0 : _a.getColumnDef(field.name);
            if (this.fields && !this.fields.includes(field.name)) {
                return [];
            }
            return Object.assign(Object.assign({ field: field.name }, colDefBase), colDefExtras);
        });
        const colDefsMergedWithTemplateDefs = this.agGrid.mergeAllColumnDefsAndStates(colDefsFromGenesisData, true);
        return colDefsMergedWithTemplateDefs;
    }
    buildCriteria() {
        var _a;
        const initialCriteria = ((_a = this.criteria) === null || _a === void 0 ? void 0 : _a.split(criteriaDelimiter)) || [];
        const criteria = initialCriteria.concat(Array.from(this.criteriaFromFilters.values()));
        return normaliseCriteria(criteria.join(criteriaJoin), criteriaDelimiter);
    }
    setFilter(fieldName, newFilter) {
        this.criteriaFromFilters.set(fieldName, newFilter);
        this.update.next(this.criteriaFromFilters);
    }
    removeFilter(fieldName) {
        this.criteriaFromFilters.delete(fieldName);
        this.update.next(this.criteriaFromFilters);
    }
};
__decorate([
    Auth
], GridProGenesisDatasource.prototype, "auth", void 0);
__decorate([
    Datasource
], GridProGenesisDatasource.prototype, "datasource", void 0);
__decorate([
    attr({ attribute: 'polling-interval' })
], GridProGenesisDatasource.prototype, "pollingInterval", void 0);
__decorate([
    attr({ mode: 'boolean', attribute: 'disable-polling' })
], GridProGenesisDatasource.prototype, "disablePolling", void 0);
__decorate([
    observable
], GridProGenesisDatasource.prototype, "request", void 0);
__decorate([
    attr({ attribute: 'row-id' })
], GridProGenesisDatasource.prototype, "rowIdAttr", void 0);
__decorate([
    attr({ mode: 'boolean', attribute: 'restart-on-reconnection' })
], GridProGenesisDatasource.prototype, "restartOnReconnection", void 0);
__decorate([
    observable
], GridProGenesisDatasource.prototype, "rowDataMapper", void 0);
GridProGenesisDatasource = __decorate([
    customElement({
        name: 'grid-pro-genesis-datasource',
    })
], GridProGenesisDatasource);
export { GridProGenesisDatasource };
